import { useEffect, useState } from 'react'
import { jsonrpc } from '~/shared/api/jsonrpc'
import { CALENDAR, PeriodFigures } from '~/team_tools/dashboard/statistic/figures'
import { useWindowParams } from '~/team_tools/dashboard/statistic/period'
import { useZone } from '~/team_tools/components/timezone'
import { Box, Header } from '~/shared/ui'
import { usePeopleSeries } from './people_series'

/** Calendar windows, plus the rolling ones the endpoint still answers for
 *  anywhere outside the dashboard. `named_intervals_filter` takes any of
 *  these against any date field, so asking for all six costs one query. */
const PERIODS = ['total', 'today', 'week', 'month', '24_hours', '7_days', '30_days']

const useActive = (lifetime?: string) => {
    const [accounts, setAccounts] = useState<Record<string, number>>({})
    const [zone] = useZone()
    const window = useWindowParams()

    useEffect(() => {
        let live = true
        jsonrpc('statistic.accounts.active_count', {
            periods: PERIODS.concat(window.custom ? ['custom'] : []),
            lifetime,
            tz: zone,
            ...window.figure,
        }).then((res) => live && setAccounts(res.result || {}))
        return () => {
            live = false
        }
    }, [lifetime, zone, window.key])

    return accounts
}

export const AccountsActiveCount = () => {
    const accounts = useActive()
    // Accounts that already existed by then, as the small line -- the same
    // shape as every other People figure.
    const older = useActive('30_days')
    // The figure is by last visit, which keeps no history; the chart is the
    // closest daily reading there is -- accounts that signed in each day.
    const { history } = usePeopleSeries('logins')
    const count = (period: string) =>
        accounts[period] !== undefined ? Number(accounts[period]).toLocaleString('en-US') : undefined

    return (
        <Box className='w-full text-center'>
            <Header>Active Site Users</Header>
            {/* D-097: website visits only, by choice (a game login stamps
                nothing here), and public players only, like every figure. */}
            <div className='text-xs text-white/40'>
                Website visits only. Public players: staff, test and fraud-tagged accounts excluded.
            </div>
            <PeriodFigures
                periods={CALENDAR}
                loading={accounts.total === undefined}
                value={count}
                note={(period) =>
                    older[period] !== undefined
                        ? `${Number(older[period]).toLocaleString('en-US')} older than 30 days`
                        : undefined
                }
                total={{
                    label: 'All time',
                    value: count('total'),
                    note:
                        older.total !== undefined
                            ? `${Number(older.total).toLocaleString('en-US')} older than 30 days`
                            : undefined,
                }}
                chart={{ lines: [{ label: 'Signed in', colour: '#38bdf8', history }] }}
            />
        </Box>
    )
}
